import { css } from 'lit';

export default css`
  :host {
    display: block;
  }

  /** The popup */
  .combobox {
    flex: 1 1 auto;
    display: inline-flex;
    width: 100%;
    position: relative;
    vertical-align: middle;
  }

  .combobox::part(popup) {
    z-index: var(--webmodule-z-popup);
  }

  .combobox[data-current-placement^='top']::part(popup) {
    transform-origin: bottom;
  }

  .combobox[data-current-placement^='bottom']::part(popup) {
    transform-origin: top;
  }

  /* The box that wraps the prefix, input, clear, and expand icon */
  .combobox__box {
    flex: 1;
    display: flex;
    width: 100%;
    min-width: 0;
    position: relative;
    align-items: center;
    justify-content: start;
    font-family: var(--webmodule-control-font-family);
    font-weight: var(--webmodule-control-font-weight);
    letter-spacing: var(--webmodule-control-letter-spacing);
    vertical-align: middle;
    overflow: hidden;
    cursor: text;
    transition:
      var(--webmodule-animate-fast) color,
      var(--webmodule-animate-fast) border,
      var(--webmodule-animate-fast) box-shadow,
      var(--webmodule-animate-fast) background-color;
  }

  /* Editable display input */
  .combobox__display-input {
    position: relative;
    width: 100%;
    font: inherit;
    border: none;
    background: none;
    color: var(--webmodule-control-color);
    cursor: text;
    overflow: hidden;
    padding: 0;
    margin: 0;
    -webkit-appearance: none;
  }

  .combobox__display-input::placeholder {
    color: var(--webmodule-control-placeholder-color);
  }

  .combobox:not(.combobox--disabled):hover .combobox__display-input {
    color: var(--webmodule-control-color-hover);
  }

  .combobox__display-input:focus {
    outline: none;
  }

  /* Standard appearance */
  .combobox--standard .combobox__box {
    background-color: var(--webmodule-control-background-color);
    border: solid var(--webmodule-control-border-width) var(--webmodule-control-border-color);
  }

  .combobox--standard.combobox--disabled .combobox__box {
    background-color: var(--webmodule-control-background-color-disabled);
    border-color: var(--webmodule-control-border-color-disabled);
    color: var(--webmodule-control-color-disabled);
    opacity: 0.5;
    cursor: not-allowed;
    outline: none;
  }

  .combobox--standard:not(.combobox--disabled).combobox--open .combobox__box,
  .combobox--standard:not(.combobox--disabled).combobox--focused .combobox__box {
    background-color: var(--webmodule-control-background-color-focus);
    border-color: var(--webmodule-control-border-color-focus);
    box-shadow: 0 0 0 var(--webmodule-focus-border-width) var(--webmodule-control-focus-ring-color);
  }

  /* Filled appearance */
  .combobox--filled .combobox__box {
    border: none;
    background-color: var(--webmodule-control-filled-background-color);
    color: var(--webmodule-control-color);
  }

  .combobox--filled:hover:not(.combobox--disabled) .combobox__box {
    background-color: var(--webmodule-control-filled-background-color-hover);
  }

  .combobox--filled.combobox--disabled .combobox__box {
    background-color: var(--webmodule-control-filled-background-color-disabled);
    opacity: 0.5;
    cursor: not-allowed;
  }

  .combobox--filled:not(.combobox--disabled).combobox--open .combobox__box,
  .combobox--filled:not(.combobox--disabled).combobox--focused .combobox__box {
    background-color: var(--webmodule-control-filled-background-color-focus);
    outline: var(--webmodule-focus-border);
  }

  /* Sizes */
  .combobox--small .combobox__box {
    border-radius: var(--webmodule-control-border-radius-small);
    font-size: var(--webmodule-control-font-size-small);
    min-height: var(--webmodule-control-height-small);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-small);
  }

  .combobox--small .combobox__clear {
    margin-inline-start: var(--webmodule-control-spacing-small);
  }

  .combobox--small .combobox__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-small);
  }

  .combobox--medium .combobox__box {
    border-radius: var(--webmodule-control-border-radius-medium);
    font-size: var(--webmodule-control-font-size-medium);
    min-height: var(--webmodule-control-height-medium);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-medium);
  }

  .combobox--medium .combobox__clear {
    margin-inline-start: var(--webmodule-control-spacing-medium);
  }

  .combobox--medium .combobox__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-medium);
  }

  .combobox--large .combobox__box {
    border-radius: var(--webmodule-control-border-radius-large);
    font-size: var(--webmodule-control-font-size-large);
    min-height: var(--webmodule-control-height-large);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-large);
  }

  .combobox--large .combobox__clear {
    margin-inline-start: var(--webmodule-control-spacing-large);
  }

  .combobox--large .combobox__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-large);
  }

  /* Pills */
  .combobox--pill.combobox--small .combobox__box {
    border-radius: var(--webmodule-control-height-small);
  }

  .combobox--pill.combobox--medium .combobox__box {
    border-radius: var(--webmodule-control-height-medium);
  }

  .combobox--pill.combobox--large .combobox__box {
    border-radius: var(--webmodule-control-height-large);
  }

  /* Prefix */
  .combobox__prefix {
    flex: 0;
    display: inline-flex;
    align-items: center;
    color: var(--webmodule-control-placeholder-color);
  }

  /* Clear button */
  .combobox__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    color: var(--webmodule-control-icon-color);
    border: none;
    background: none;
    padding: 0;
    transition: var(--webmodule-animate-fast) color;
    cursor: pointer;
  }

  .combobox__clear:hover {
    color: var(--webmodule-control-icon-color-hover);
  }

  .combobox__clear:focus {
    outline: none;
  }

  /* Expand icon */
  .combobox__expand-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    transition: var(--webmodule-animate-medium) rotate ease;
    rotate: 0;
    margin-inline-start: var(--webmodule-spacing-small);
  }

  .combobox--open .combobox__expand-icon {
    rotate: -180deg;
  }

  /* Listbox */
  .combobox__listbox {
    display: block;
    position: relative;
    font-family: var(--webmodule-font-sans);
    font-size: var(--webmodule-font-size-medium);
    font-weight: var(--webmodule-font-weight-normal);
    box-shadow: var(--webmodule-shadow-large);
    background: var(--webmodule-panel-background-color);
    border: solid var(--webmodule-panel-border-width) var(--webmodule-panel-border-color);
    border-radius: var(--webmodule-border-radius-medium);
    padding-block: var(--webmodule-spacing-x-small);
    padding-inline: 0;
    overflow: auto;
    overscroll-behavior: none;

    /* Comes from POPUP */
    max-width: var(--auto-size-available-width);
    max-height: var(--auto-size-available-height);
  }

  .combobox__listbox ::slotted(webmodule-divider) {
    --spacing: var(--webmodule-spacing-x-small);
  }

  /* "Create ..." row and empty message - rendered in the shadow root, not as options */
  .combobox__create,
  .combobox__empty {
    font-family: var(--webmodule-font-sans);
    font-size: var(--webmodule-font-size-medium);
    padding-block: var(--webmodule-spacing-x-small);
    padding-inline: var(--webmodule-spacing-medium);
  }

  .combobox__create {
    display: flex;
    align-items: center;
    gap: var(--webmodule-spacing-x-small);
    cursor: pointer;
    color: var(--webmodule-control-color);
  }

  .combobox__create:hover,
  .combobox__create--current {
    background-color: var(--webmodule-control-background-color-hover, var(--webmodule-color-neutral-100));
  }

  .combobox__create-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .combobox__empty {
    color: var(--webmodule-color-neutral-500);
    cursor: default;
  }
`;
